import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useChatContext, useUnifiedData } from './AppDataContext';
import { useWindow } from '../contexts/WindowContext';
import { useToast } from './ToastContext';
import { useAuth } from './AuthContext';
import { useQuery } from 'convex/react';

import { Quiz, quizToResource } from '../helpers/quizHelper';
import { wrapContentInResourceTags } from '../components/Resources/utils';
import { parseArtifact } from '../handler/TextRenderer/utils';

import { ConvexMessage, MessageDocument } from '../types/message';
import { Resource, ResourceType } from '../types/resource';
import { getCodeLanguageByExtension } from '../hooks/FileHandler/utils';
import { useAttachmentContext } from './AttachmentContext';

interface ResourceContextType {
  resources: Record<string, Resource[]>;
  currentChatResources: Resource[];
  setResources: React.Dispatch<React.SetStateAction<Record<string, Resource[]>>>;
  expandedResource: Resource | null;
  setExpandedResource: React.Dispatch<React.SetStateAction<Resource | null>>;
  isResourcesVisible: boolean;
  toggleResourceVisibility: () => void;
  openResources: () => void;
  expandedVersion: number;
  currentResourceVersions: Resource[];
  setExpandedVersion: React.Dispatch<React.SetStateAction<number>>;
  removeResourcesForMessage: (messageId: string) => void;
  isWritingResource: boolean;
  setIsWritingResource: React.Dispatch<React.SetStateAction<boolean>>;
  handleCreateResource: (newResource: Resource, chatId?: string) => void;
  /** `resourceId` wins when given (quiz links carry the quiz id); otherwise matched by title. */
  handleResourceClick: (resourceTitle: string, messageId: string, resourceId?: string) => void;
  parseAndAddTextAttachments: (files: MessageDocument[], chatIdOverride?: string) => void;
  processResources: (content: string, id: string) => Resource[];
  handleAddResourceToSpace: (resource: Resource) => Promise<void>;
  resetResourceState: () => void;
  isResourcesListVisible: boolean;
  setIsResourcesListVisible: React.Dispatch<React.SetStateAction<boolean>>;
  showResourcesButton: boolean;
  /** True while a shared chat supplies the resources: hide chat-bound actions. */
  isReadOnlyResources: boolean;
  setSharedResourceSource: (source: SharedResourceSource | null) => void;
}

/**
 * Lets a page that isn't the current chat (the shared chat view) feed its own
 * messages to the panel. While set, it stands in for the route's chat.
 */
export interface SharedResourceSource {
  chatId: string;
  messages: ConvexMessage[];
}

interface ResourceContextProviderProps {
  children: ReactNode;
}

interface StoredResourceState {
  chatId: string;
  resourceTitle: string;
  version?: number;
}

const STORAGE_KEY = 'studybuddy_resource_state';
const VISIBILITY_PREF_KEY = 'studybuddy_resource_visibility_pref';

const ResourceContext = createContext<ResourceContextType | undefined>(undefined);

export const ResourceContextProvider: React.FC<ResourceContextProviderProps> = ({ children }) => {
  const toast = useToast();
  const {
    currentChatId: routeChatId,
    currentChatSpace,
    filteredMessages: routeMessages,
  } = useChatContext();
  const [sharedSource, setSharedResourceSource] = useState<SharedResourceSource | null>(null);
  const isReadOnlyResources = sharedSource !== null;
  const currentChatId = sharedSource ? sharedSource.chatId : routeChatId;
  const filteredMessages = sharedSource ? sharedSource.messages : routeMessages;
  const { addResourceToSpace } = useUnifiedData();
  const {
    textFiles,
    activeTextAttachmentFileId,
    isTextAttachmentViewOpen,
    handleFileCardClick,
    handleTextAttachmentViewClose,
  } = useAttachmentContext();
  const { windowWidth } = useWindow();
  const location = useLocation();
  const navigate = useNavigate();

  const processedMessageIdsRef = useRef(new Set<string>());
  const messageContentHashesRef = useRef<Record<string, string>>({});
  const processedAttachmentKeysRef = useRef<Set<string>>(new Set());
  // Tracks the chatId of the most-recently expanded resource. Used by the
  // persistence effect to distinguish "user closed the resource in this chat"
  // (clear localStorage) from "navigation transient cleared expandedResource"
  // (preserve localStorage so reload still restores).
  const lastExpandedChatRef = useRef<string | null>(null);
  // Watermark for the resource currently visible in the panel. This lets us
  // distinguish a version that was already present when the resource opened
  // from a genuinely new version that arrived while it remained in view.
  const viewedResourceVersionRef = useRef<{ key: string; latestVersion: number } | null>(null);

  const [resources, setResources] = useState<Record<string, Resource[]>>({});
  const [expandedResource, setExpandedResource] = useState<Resource | null>(null);
  const [isResourcesVisible, setIsResourcesVisible] = useState<boolean>(() => {
    const storedPref = localStorage.getItem(VISIBILITY_PREF_KEY);
    return storedPref !== null ? storedPref === 'true' : windowWidth > 768;
  });
  const [expandedVersion, setExpandedVersion] = useState<number>(0);
  const [isWritingResource, setIsWritingResource] = useState<boolean>(false);
  const [isResourcesListVisible, setIsResourcesListVisible] = useState<boolean>(false);

  const [restoredState, setRestoredState] = useState<StoredResourceState | null>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
  });

  // Quizzes are owner-only, so shared chats don't load them.
  const auth = useAuth();
  const chatQuizzes: Quiz[] | undefined = useQuery(
    "quizzes:listByChat" as any,
    !sharedSource && routeChatId && routeChatId !== 'new' && auth.user?._id
      ? { chatId: routeChatId, userId: String(auth.user._id), legacyUserId: auth.user.legacyId }
      : "skip"
  );

  /**
   * Resets all refs tracking processed messages when the chat changes.
   */
  useEffect(() => {
    processedMessageIdsRef.current.clear();
    messageContentHashesRef.current = {};
    processedAttachmentKeysRef.current.clear();
  }, [currentChatId]);

  /**
   * Memoized list of resources for the currently active chat.
   */
  const currentChatResources = useMemo(
    () => (currentChatId ? resources[currentChatId] || [] : []),
    [resources, currentChatId]
  );

  /**
   * Memoized list of all available versions for the currently expanded resource.
   */
  const currentResourceVersions = useMemo(
    () => currentChatResources.filter(resource => resource.title === expandedResource?.title),
    [currentChatResources, expandedResource]
  );

  const hasExpandedResourceForCurrentChat = Boolean(
    expandedResource &&
    currentChatId &&
    currentChatId !== 'new' &&
    expandedResource.chatId === currentChatId
  );

  const hasActiveAttachment = Boolean(
    isTextAttachmentViewOpen &&
    activeTextAttachmentFileId &&
    textFiles.some(file => file.id === activeTextAttachmentFileId)
  );
  const effectiveResourcesVisible =
    hasActiveAttachment || (isResourcesVisible && hasExpandedResourceForCurrentChat);

  /**
   * Follow new versions while a resource is actively visible.
   *
   * The first observation only establishes a watermark, so explicitly
   * opening an older version is respected. If a higher version arrives after
   * that, both the context selection and ExpandedResource's desired version
   * advance to the latest entry.
   */
  useEffect(() => {
    const isViewingResource =
      isResourcesVisible &&
      hasExpandedResourceForCurrentChat &&
      !hasActiveAttachment &&
      expandedResource;

    if (!isViewingResource) {
      viewedResourceVersionRef.current = null;
      return;
    }

    const matchingVersions = currentResourceVersions.filter(
      resource => resource.type === expandedResource.type
    );
    if (matchingVersions.length === 0) return;

    const latest = matchingVersions.reduce((candidate, resource) =>
      (resource.version || 0) > (candidate.version || 0) ? resource : candidate
    );
    const latestVersion = latest.version || 0;
    const key = `${currentChatId}:${expandedResource.type}:${expandedResource.title}`;
    const observed = viewedResourceVersionRef.current;

    if (!observed || observed.key !== key) {
      viewedResourceVersionRef.current = { key, latestVersion };
      return;
    }

    viewedResourceVersionRef.current = { key, latestVersion };
    if (latestVersion <= observed.latestVersion) return;

    setExpandedResource(latest);
    setExpandedVersion(latestVersion);
  }, [
    currentChatId,
    currentResourceVersions,
    expandedResource,
    hasActiveAttachment,
    hasExpandedResourceForCurrentChat,
    isResourcesVisible,
  ]);

  /**
   * A simple string hashing function to detect content changes.
   */
  const hashContent = (content: string): string => {
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash |= 0; // Convert to 32bit integer
    }
    return hash.toString();
  };

  /**
   * Parses artifact definitions from a string, ignoring any content within <think> tags.
   * This is a pure function that does not modify state.
   */
  const processResources = useCallback((content: string, messageId: string): Resource[] => {
    if (typeof content !== 'string' || !currentChatId) {
      return [];
    }
    const contentWithoutThinking = content.replace(/<think>[\s\S]*?<\/think>/g, '');
    const resourceTagRegex = /<(?:artifacts|resource)>([\s\S]*?)<\/(?:artifacts|resource)>/g;
    const parsedResources: Resource[] = [];
    let match;
    let count = 0;
    while ((match = resourceTagRegex.exec(contentWithoutThinking)) !== null) {
      const resourceContent = match[1];
      const parsed = parseArtifact(resourceContent, `${messageId}-${count}`, count, false, messageId);
      if (parsed) {
        parsedResources.push({ ...parsed, chatId: currentChatId, messageId });
      }
      count++;
    }
    return parsedResources;
  }, [currentChatId]);

  /**
   * Processes filteredMessages (active branch only) to extract resources.
   * Tracks content hashes to avoid redundant parsing, and prunes resources
   * from messages that have left the active branch.
   *
   * Pruning is gated on the set of messageIds we've *previously processed*
   * for this chat. A messageId we've never seen is treated as "unknown,
   * leave alone" rather than "stale, remove" -- so a transient gap in
   * filteredMessages (e.g. mid-load, or briefly empty during branch swap)
   * can't wipe state we'd otherwise want to keep.
   */
  useEffect(() => {
    if (!filteredMessages || !currentChatId) return;

    const visibleMessageIds = new Set(
      filteredMessages.map(m => m._id).filter(Boolean) as string[]
    );

    // Snapshot before mutating the ref below -- we use it both to decide
    // which tracking entries to drop and (in the setter) which resources
    // are safe to prune.
    const previouslySeen = new Set(processedMessageIdsRef.current);

    const messagesToUpdate: { message: ConvexMessage; newHash: string; oldHash: string | undefined }[] = [];

    filteredMessages.forEach((message: ConvexMessage) => {
      if (!message?._id || message.chatId !== currentChatId) return;

      const newHash = message.content ? hashContent(message.content) : '';
      const oldHash = messageContentHashesRef.current[message._id];

      if (!processedMessageIdsRef.current.has(message._id) || (newHash !== oldHash && newHash !== '')) {
        messagesToUpdate.push({ message, newHash, oldHash });
        processedMessageIdsRef.current.add(message._id);
        messageContentHashesRef.current[message._id] = newHash;
      }
    });

    // Drop tracking entries for messages that legitimately left the branch.
    for (const id of previouslySeen) {
      if (!visibleMessageIds.has(id)) {
        processedMessageIdsRef.current.delete(id);
        delete messageContentHashesRef.current[id];
      }
    }

    setResources(prev => {
      const newResources = { ...prev };
      let chatResources = newResources[currentChatId] ? [...newResources[currentChatId]] : [];
      let hasChanged = false;

      // Prune resources whose owning message we've previously seen but is
      // now missing. Keep manually-created (no messageId) resources, and
      // keep entries whose messageId is unknown to us -- those represent
      // either transient load gaps or state restored from a prior chat
      // visit, neither of which justifies deletion.
      const beforePrune = chatResources.length;
      chatResources = chatResources.filter(r => {
        if (!r.messageId) return true;
        if (visibleMessageIds.has(r.messageId)) return true;
        return !previouslySeen.has(r.messageId);
      });
      if (chatResources.length !== beforePrune) hasChanged = true;

      // Process new or changed messages
      messagesToUpdate.forEach(({ message, newHash, oldHash }) => {
        if (newHash !== oldHash && oldHash) {
          const originalSize = chatResources.length;
          chatResources = chatResources.filter(r => r.messageId !== message._id);
          if (chatResources.length !== originalSize) hasChanged = true;
        }

        if (message.isAi && message.content) {
          const parsed = processResources(message.content, message._id);
          if (parsed.length > 0) {
            parsed.forEach(resource => {
              const sameTypeResources = chatResources.filter(r => r.title === resource.title && r.type === resource.type);
              const isDuplicate = sameTypeResources.some(r => r.content === resource.content);
              if (!isDuplicate) {
                const maxVersion = sameTypeResources.reduce((max, r) => Math.max(max, r.version || 0), 0);
                // Clone so repeated invocations of this state updater (which
                // React 18 may do) don't compound the version on the same
                // source object reference.
                chatResources.push({ ...resource, version: maxVersion + 1 });
                hasChanged = true;
              }
            });
          }
        }
      });

      if (hasChanged) {
        newResources[currentChatId] = chatResources;
        return newResources;
      }
      return prev;
    });
  }, [filteredMessages, currentChatId, processResources]);

  /**
   * Keeps `expandedResource` pointing at the freshest reference of itself
   * in `currentChatResources`. Two cases:
   *   1. Same-chat refresh: a streaming message rewrote its content, so
   *      our artifact got re-pushed with a new version. The captured
   *      reference is now stale.
   *   2. Cross-chat swap: user navigated to a chat that has a same-titled
   *      resource. Switch to the new chat's latest version so the side
   *      panel actually reflects the active chat.
   */
  useEffect(() => {
    if (!expandedResource) return;

    if (currentChatId && expandedResource.chatId !== currentChatId) {
      const sameTitle = currentChatResources.filter(r => r.title === expandedResource.title);
      if (sameTitle.length > 0) {
        const latest = sameTitle.reduce((acc, r) => ((r.version || 0) > (acc.version || 0) ? r : acc));
        if (latest !== expandedResource) {
          setExpandedResource(latest);
          if (latest.version != null) setExpandedVersion(latest.version);
        }
      }
      return;
    }

    const fresh =
      currentChatResources.find(r => r.id === expandedResource.id) ||
      currentChatResources.find(r =>
        r.title === expandedResource.title &&
        r.messageId === expandedResource.messageId &&
        r.type === expandedResource.type
      );

    if (
      fresh &&
      fresh !== expandedResource &&
      (fresh.content !== expandedResource.content || fresh.version !== expandedResource.version)
    ) {
      setExpandedResource(fresh);
      if (fresh.version != null) setExpandedVersion(fresh.version);
    }
  }, [currentChatResources, expandedResource, currentChatId]);

  /**
   * Manually adds a new resource, handling versioning and preventing duplicates.
   * Used for non-AI generated resources like PDF uploads.
   */
  const handleCreateResource = useCallback((newResource: Resource, chatId: string = currentChatId || '') => {
    if (!chatId) return;
    setResources(prev => {
      const updated = { ...prev };
      const chatResources = updated[chatId] ? [...updated[chatId]] : [];

      const sameType = chatResources.filter(r => r.title === newResource.title && r.type === newResource.type);

      // For URL-keyed resources (e.g. uploaded attachments) identity is the
      // remote URL: two attachments sharing a filename are distinct resources
      // as long as their URLs differ. For in-chat resources (AI artifacts,
      // quizzes) identity remains content-based.
      if (newResource.url) {
        if (sameType.some(r => r.url === newResource.url)) {
          return prev;
        }
      } else if (sameType.some(r => r.content === newResource.content)) {
        return prev; // Exact duplicate exists
      }

      const maxVersion = sameType.reduce((max, r) => Math.max(max, r.version || 0), 0);
      // Clone so we never mutate the caller's object. Mutating `newResource`
      // caused repeated calls that share a reference (rare, but possible if
      // callers pass the same object twice) to compound version numbers.
      const resourceToStore: Resource = {
        ...newResource,
        version: maxVersion + 1,
        chatId,
      };

      updated[chatId] = [...chatResources, resourceToStore];
      return updated;
    });
  }, [currentChatId]);

  /**
   * Registers uploaded text/PDF attachments as resources in the side panel.
   * Deterministic ids derived from the URL make repeated calls idempotent
   * (guarded further by URL-based dedupe in `handleCreateResource`).
   */
  const parseAndAddTextAttachments = useCallback((files: MessageDocument[], chatIdOverride?: string) => {
    const chatId = chatIdOverride || currentChatId || '';
    if (!files || files.length === 0 || !chatId) return;
    try {
      files.forEach(file => {
        if (!file) return;
        const idKey = file.url || `${file.title}:${file.type}`;
        const isCode = file.type === 'code';
        // DocumentType ('pdf' | 'doc' | 'code') is a structural subset of
        // ResourceType, so a single cast is sufficient and safe.
        handleCreateResource({
          title: file.title,
          // Preserve any locally-extracted content (populated on the send path
          // for txt/md/csv/code/docx/pptx/xlsx). Empty string when the
          // attachment is being rehydrated from storage -- in that case
          // `ContentDisplayView` lazily fetches the file body from `url`.
          content: file.content || '',
          type: file.type as ResourceType,
          chatId,
          version: 1,
          id: `file-${idKey}`,
          timestamp: new Date().toISOString(),
          url: file.url,
          // Derive syntax-highlighting language from the filename for code
          // attachments so the resource panel highlights correctly even when
          // the content is fetched lazily from the URL.
          ...(isCode ? { language: getCodeLanguageByExtension(file.title) } : {}),
        }, chatId);
      });
    } catch (error) {
      console.error('Error processing text attachments:', error);
    }
  }, [handleCreateResource, currentChatId]);

  /**
   * Registers the chat's quizzes as resources. TestView loads each quiz's
   * questions and answers itself.
   */
  useEffect(() => {
    if (!chatQuizzes || !currentChatId || sharedSource) return;
    chatQuizzes.forEach(quiz => handleCreateResource(quizToResource(quiz, currentChatId), currentChatId));
  }, [chatQuizzes, currentChatId, sharedSource, handleCreateResource]);

  /**
   * Creates resources from text attachments across messages. Tracks per-message
   * attachment keys so re-renders (e.g. during streaming) don't recreate
   * resources every tick, and prunes the tracking set to keys still present
   * so it can't grow unbounded across a long-lived chat session.
   */
  useEffect(() => {
    if (!currentChatId || !filteredMessages) return;
    const newDocs: MessageDocument[] = [];
    const liveKeys = new Set<string>();
    for (const message of filteredMessages) {
      if (
        !message?._id ||
        message.chatId !== currentChatId ||
        !message.textAttachments?.length
      ) continue;
      for (const doc of message.textAttachments) {
        const key = `${currentChatId}:${message._id}:${doc.url || doc.title}:${doc.type}`;
        liveKeys.add(key);
        if (processedAttachmentKeysRef.current.has(key)) continue;
        processedAttachmentKeysRef.current.add(key);
        newDocs.push(doc);
      }
    }
    // GC keys for attachments no longer present (e.g. message edited).
    for (const key of processedAttachmentKeysRef.current) {
      if (!liveKeys.has(key)) processedAttachmentKeysRef.current.delete(key);
    }
    if (newDocs.length > 0) {
      parseAndAddTextAttachments(newDocs, currentChatId);
    }
  }, [filteredMessages, currentChatId, parseAndAddTextAttachments]);

  /**
   * Persists the currently expanded resource to localStorage.
   *
   * Three states matter:
   *   - (currentChatId, expandedResource for that chat) -> save.
   *   - (currentChatId, expandedResource is null AND we just had one for
   *     this chat) -> user closed it; clear.
   *   - any other combination (transient null currentChatId, expanded
   *     belongs to a different chat) -> leave localStorage alone so
   *     reloads still restore correctly.
   */
  useEffect(() => {
    // Shared chats aren't the viewer's; don't reopen them on reload.
    if (!currentChatId || isReadOnlyResources) return;

    if (expandedResource && expandedResource.chatId === currentChatId) {
      const stateToStore: StoredResourceState = {
        chatId: currentChatId,
        resourceTitle: expandedResource.title,
        version: expandedResource.version,
      };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToStore));
      } catch {
        // storage unavailable or full -- non-fatal
      }
      lastExpandedChatRef.current = currentChatId;
      return;
    }

    if (!expandedResource) {
      // Only clear if the previously-expanded resource was for THIS chat,
      // i.e. the user actively closed it. If it was for a different chat,
      // we're in a navigation transition and must preserve the saved state.
      if (lastExpandedChatRef.current && lastExpandedChatRef.current === currentChatId) {
        localStorage.removeItem(STORAGE_KEY);
      }
      lastExpandedChatRef.current = null;
    }
    // expandedResource exists but for a different chat: do nothing.
  }, [expandedResource, currentChatId, isReadOnlyResources]);

  /**
   * Restores the expanded resource from localStorage when its chat is loaded.
   */
  useEffect(() => {
    if (!restoredState || !currentChatId) return;
    if (currentChatId !== restoredState.chatId) return;
    if (!resources[currentChatId] || resources[currentChatId].length === 0) return;

    // Match on title + version so multi-version resources restore the exact
    // version that was open. Falls back to the latest version of that title
    // if the persisted version is missing or no longer exists.
    const sameTitle = resources[currentChatId].filter(r => r.title === restoredState.resourceTitle);
    if (sameTitle.length === 0) return;
    let resourceToExpand: Resource | undefined;
    if (restoredState.version !== undefined) {
      resourceToExpand = sameTitle.find(r => r.version === restoredState.version);
    }
    if (!resourceToExpand) {
      resourceToExpand = sameTitle.reduce((acc, r) =>
        ((r.version || 0) > (acc.version || 0) ? r : acc)
      );
    }
    setExpandedResource(resourceToExpand);
    setExpandedVersion(resourceToExpand.version || 0);
    setIsResourcesVisible(true);
    setRestoredState(null); // Consume the restored state
  }, [restoredState, currentChatId, resources]);

  /**
   * Handles opening a resource from a link/navigation state. Uses the
   * router's navigate() to clear consumed state so router state stays in
   * sync (window.history.replaceState bypasses the router).
   */
  useEffect(() => {
    const stateTitle = (location.state as { title?: string } | null)?.title;
    if (!stateTitle || !currentChatId || !resources[currentChatId]) return;
    const resource = resources[currentChatId].find(r => r.title === stateTitle);
    if (!resource) return;
    setExpandedResource(resource);
    setExpandedVersion(resource.version || 0);
    setIsResourcesVisible(true);
    navigate(location.pathname + location.search + location.hash, { replace: true, state: null });
  }, [currentChatId, location, resources, navigate]);

  const removeResourcesForMessage = useCallback((messageId: string) => {
    setResources(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(chatKey => {
        updated[chatKey] = updated[chatKey].filter(resource => resource.messageId !== messageId);
      });
      return updated;
    });
  }, []);

  const toggleResourceVisibility = useCallback(() => {
    if (hasActiveAttachment) {
      handleTextAttachmentViewClose();
      setIsResourcesVisible(false);
      setIsResourcesListVisible(false);
      try {
        localStorage.setItem(VISIBILITY_PREF_KEY, 'false');
      } catch {
        // storage unavailable -- non-fatal
      }
      return;
    }
    const fallbackResource = currentChatId ? resources[currentChatId]?.[resources[currentChatId].length - 1] : undefined;
    const resourceToOpen = hasExpandedResourceForCurrentChat ? expandedResource : fallbackResource;
    const newValue = effectiveResourcesVisible ? false : Boolean(resourceToOpen);

    if (newValue && resourceToOpen && !hasExpandedResourceForCurrentChat) {
      setExpandedResource(resourceToOpen);
      setExpandedVersion(resourceToOpen.version || 0);
    }

    setIsResourcesVisible(newValue);
    try {
      localStorage.setItem(VISIBILITY_PREF_KEY, String(newValue));
    } catch {
      // storage unavailable -- non-fatal
    }
  }, [
    currentChatId,
    expandedResource,
    hasExpandedResourceForCurrentChat,
    effectiveResourcesVisible,
    resources,
    hasActiveAttachment,
    handleTextAttachmentViewClose,
  ]);

  const openResources = useCallback(() => {
    const fallbackResource = currentChatId ? resources[currentChatId]?.[resources[currentChatId].length - 1] : undefined;
    const resourceToOpen = hasExpandedResourceForCurrentChat ? expandedResource : fallbackResource;

    if (!resourceToOpen) {
      const pendingFile = [...textFiles].reverse().find(file => file.isLoaded);
      if (pendingFile) {
        handleFileCardClick(pendingFile);
      } else {
        setIsResourcesVisible(false);
        setIsResourcesListVisible(false);
      }
      return;
    }

    setExpandedResource(resourceToOpen);
    setExpandedVersion(resourceToOpen.version || 0);
    setIsResourcesVisible(true);
  }, [
    currentChatId,
    expandedResource,
    hasExpandedResourceForCurrentChat,
    resources,
    textFiles,
    handleFileCardClick,
  ]);

  const handleResourceClick = useCallback((resourceTitle: string, messageId: string, resourceId?: string) => {
    if (!currentChatId || !resources[currentChatId]) return;

    const byId = resourceId ? resources[currentChatId].find(r => r.id === resourceId) : undefined;
    const foundResources = byId ? [byId] : resources[currentChatId].filter(r => r.title === resourceTitle);
    if (foundResources.length === 0) return;

    let selectedResource = byId ?? foundResources.find(r => r.messageId === messageId);
    if (!selectedResource) {
      selectedResource = foundResources.reduce((latest, current) =>
        (current.version || 1) > (latest.version || 1) ? current : latest
      );
    }

    if (hasActiveAttachment) handleTextAttachmentViewClose();
    setIsResourcesVisible(true);
    setExpandedVersion(selectedResource.version || 0);
    setExpandedResource({ ...selectedResource });
    setIsResourcesListVisible(false);
  }, [currentChatId, resources, hasActiveAttachment, handleTextAttachmentViewClose]);

  const handleAddResourceToSpace = useCallback(async (resource: Resource) => {
    if (!currentChatSpace?.id) {
      toast('No space selected', 'error');
      return;
    }
    try {
      const wrappedContent = wrapContentInResourceTags(resource, currentChatId || '');
      if (!wrappedContent) {
        toast('Failed to process resource', 'error');
        return;
      }
      await addResourceToSpace(currentChatSpace.id, wrappedContent);
      toast('Added to space successfully', 'success');
    } catch (error) {
      console.error('Failed to add artifact to space:', error);
      toast('Failed to add to space', 'error');
    }
  }, [currentChatSpace?.id, currentChatId, addResourceToSpace, toast]);

  /**
   * Clears in-memory expanded state. Does NOT touch localStorage -- the
   * persistence effect handles writes/clears based on whether the closure
   * is happening inside the same chat that owned the resource. Callers can
   * thus safely invoke this during chat transitions without nuking saved
   * state.
   */
  const resetResourceState = useCallback(() => {
    setExpandedResource(null);
    setExpandedVersion(0);
    setIsResourcesVisible(false);
  }, []);

  /**
   * Resets the in-memory expanded state when the expanded resource is no
   * longer available in the active chat — including the new-chat route
   * (no `currentChatId`) and the explicit `'new'` placeholder, which both
   * mean "no chat in view" and should drop a stale expansion carried over
   * from the previous chat. localStorage is left intact by the persistence
   * effect, so restoring on full reload to the original chat still works.
   */
  useEffect(() => {
    if (!currentChatId || currentChatId === 'new') {
      if (expandedResource || isResourcesVisible || isResourcesListVisible) {
        resetResourceState();
        setIsResourcesListVisible(false);
      }
      return;
    }
    if (expandedResource && expandedResource.chatId !== currentChatId) {
      const resourceExistsInNewChat = currentChatResources.some(r => r.title === expandedResource.title);
      if (!resourceExistsInNewChat) {
        resetResourceState();
      }
    }
  }, [
    currentChatId,
    expandedResource,
    currentChatResources,
    isResourcesVisible,
    isResourcesListVisible,
    resetResourceState,
  ]);

  const showResourcesButton =
    (currentChatResources.length > 0 || textFiles.some(file => file.isLoaded)) &&
    !effectiveResourcesVisible;

  /**
   * Memoize the context value so consumers re-render only when one of the
   * tracked values actually changes (state setters are stable references
   * from React, so they don't need to participate in the dep list).
   */
  const contextValue = useMemo<ResourceContextType>(() => ({
    resources,
    currentChatResources,
    setResources,
    expandedResource,
    setExpandedResource,
    isResourcesVisible: effectiveResourcesVisible,
    toggleResourceVisibility,
    openResources,
    expandedVersion,
    currentResourceVersions,
    setExpandedVersion,
    removeResourcesForMessage,
    isWritingResource,
    setIsWritingResource,
    handleCreateResource,
    parseAndAddTextAttachments,
    handleResourceClick,
    processResources,
    handleAddResourceToSpace,
    resetResourceState,
    isResourcesListVisible,
    setIsResourcesListVisible,
    showResourcesButton,
    isReadOnlyResources,
    setSharedResourceSource,
  }), [
    resources,
    currentChatResources,
    expandedResource,
    effectiveResourcesVisible,
    toggleResourceVisibility,
    openResources,
    expandedVersion,
    currentResourceVersions,
    removeResourcesForMessage,
    isWritingResource,
    handleCreateResource,
    parseAndAddTextAttachments,
    handleResourceClick,
    processResources,
    handleAddResourceToSpace,
    resetResourceState,
    isResourcesListVisible,
    showResourcesButton,
    isReadOnlyResources,
  ]);

  return (
    <ResourceContext.Provider value={contextValue}>
      {children}
    </ResourceContext.Provider>
  );
};

export const useResourceContext = (): ResourceContextType => {
  const context = useContext(ResourceContext);
  if (!context) {
    throw new Error('useResourceContext must be used within a ResourceContextProvider');
  }
  return context;
};
